import { TrendingUp } from "lucide-react";

export function hotColor(v) {
  if (v >= 65) return "#39FF14";
  if (v >= 45) return "#007AFF";
  if (v >= 30) return "#FFCC00";
  return "#A1A1AA";
}

export function Bar({ label, value, testId }) {
  const pct = Math.max(0, Math.min(100, value ?? 0));
  const color = hotColor(pct);
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div className="label text-[10px] w-20 shrink-0">{label}</div>
      <div className="flex-1 h-1.5 bg-[#1A1A1A] overflow-hidden">
        <div className="h-full transition-[width] duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="mono font-bold text-sm w-12 text-right" style={{ color }}>{pct}%</div>
    </div>
  );
}

function Card({ title, children, testId }) {
  return (
    <div className="surface-raised p-6" data-testid={testId}>
      <div className="label mb-5">{title}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, color = "#FFFFFF" }) {
  return (
    <div className="flex items-end justify-between mt-3">
      <div className="label text-[10px]">{label}</div>
      <div className="heading text-2xl font-black mono" style={{ color }}>{value}</div>
    </div>
  );
}

function OutcomeBig({ label, sub, value, market }) {
  const hot = value >= 50;
  return (
    <div className={`p-4 border transition-colors ${hot ? "border-[#39FF14] bg-[#39FF14]/5" : "border-[#27272A]"}`} data-testid={`outcome-${label}`}>
      <div className="label text-[10px]">{label}</div>
      <div className="heading text-5xl font-black mono mt-2 tracking-tighter" style={{ color: hot ? "#39FF14" : "#FFFFFF" }}>
        {value}<span className="text-xl text-[#71717A]">%</span>
      </div>
      <div className="text-[10px] text-[#A1A1AA] mt-1 truncate uppercase tracking-wider font-bold">{sub}</div>
      {market != null && <div className="mt-2 text-[10px] mono text-[#71717A]">Bookmaker: {market}%</div>}
    </div>
  );
}

export default function ProbabilityWidget({ prediction, home, away, odds }) {
  const mo = prediction.match_outcome;
  const dc = prediction.double_chance || {};
  const g = prediction.goals || {};
  const c = prediction.corners || {};
  const ca = prediction.cards || {};
  const fmt = (v) => (typeof v === "number" ? v.toFixed(1) : "–");

  return (
    <div className="space-y-6" data-testid="probability-widget">
      <div className="surface-raised p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="label flex items-center gap-2"><TrendingUp size={12} strokeWidth={1.5} /> Esito Finale 1X2</div>
          <div className="label text-[10px]">Affidabilità <span className="text-[#39FF14] font-black" data-testid="confidence-value">{mo.confidence}%</span></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <OutcomeBig label="1" sub={home} value={mo.home_win} market={odds?.home_win} />
          <OutcomeBig label="X" sub="Pareggio" value={mo.draw} market={odds?.draw} />
          <OutcomeBig label="2" sub={away} value={mo.away_win} market={odds?.away_win} />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          {["1X", "12", "X2"].map((k) => (
            <div key={k} className="border border-[#27272A] px-4 py-2 flex items-center justify-between" data-testid={`double-chance-${k}`}>
              <span className="label text-[10px]">{k}</span>
              <span className="mono font-black" style={{ color: hotColor(dc[k]) }}>{dc[k] ?? "–"}%</span>
            </div>
          ))}
        </div>
        {mo.reasoning && <p className="mt-5 text-sm text-[#A1A1AA] leading-relaxed border-l-2 border-[#39FF14]/40 pl-3">{mo.reasoning}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Gol & GG/NG" testId="goals-card">
          <div className="space-y-3">
            <Bar label="Over 0.5" value={g.over_0_5} />
            <Bar label="Over 1.5" value={g.over_1_5} testId="prob-over-1-5" />
            <Bar label="Over 2.5" value={g.over_2_5} testId="prob-over-2-5" />
            <Bar label="Over 3.5" value={g.over_3_5} testId="prob-over-3-5" />
            <Bar label="Over 4.5" value={g.over_4_5} />
            <Bar label="Goal (GG)" value={g.btts_yes} testId="prob-btts" />
            <Bar label="No Goal" value={g.btts_yes != null ? 100 - g.btts_yes : null} />
          </div>
          <div className="mt-5 pt-4 border-t border-[#27272A] grid grid-cols-2 gap-4">
            <div><div className="label text-[10px] mb-1">xG Casa</div><div className="heading text-3xl font-black mono">{g.expected_goals_home?.toFixed?.(2) ?? "–"}</div></div>
            <div className="text-right"><div className="label text-[10px] mb-1">xG Ospiti</div><div className="heading text-3xl font-black mono">{g.expected_goals_away?.toFixed?.(2) ?? "–"}</div></div>
          </div>
          {odds?.over != null && <div className="mt-3 text-[10px] mono text-[#71717A]">Bookmaker Over {odds.over_under_line}: {odds.over}%</div>}
        </Card>

        <Card title="Risultato Esatto" testId="correct-scores-card">
          <div className="space-y-2">
            {(prediction.correct_scores || []).map((s) => (
              <div key={s.score} className="flex items-center justify-between border border-[#27272A] px-4 py-2.5">
                <span className="heading text-2xl font-black mono">{s.score}</span>
                <span className="mono font-bold" style={{ color: hotColor(s.probability * 4) }}>{s.probability}%</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Calci d'angolo · Pronostico AI" testId="corners-card">
          <div className="space-y-3">
            <Bar label="Over 7.5" value={c.over_7_5} />
            <Bar label="Over 8.5" value={c.over_8_5} />
            <Bar label="Over 9.5" value={c.over_9_5} />
            <Bar label="Over 10.5" value={c.over_10_5} />
            <Bar label="Over 11.5" value={c.over_11_5} />
            <Bar label="1° T >4.5" value={c.first_half_over_4_5} />
            <Bar label="Più corner casa" value={c.home_more_corners} />
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mt-4 pt-4 border-t border-[#27272A]">
            <Bar label={`${home.slice(0, 10)} >3.5`} value={c.home_over_3_5} />
            <Bar label={`${away.slice(0, 10)} >3.5`} value={c.away_over_3_5} />
            <Bar label={`${home.slice(0, 10)} >4.5`} value={c.home_over_4_5} />
            <Bar label={`${away.slice(0, 10)} >4.5`} value={c.away_over_4_5} />
            <Bar label={`${home.slice(0, 10)} >5.5`} value={c.home_over_5_5} />
            <Bar label={`${away.slice(0, 10)} >5.5`} value={c.away_over_5_5} />
          </div>
          <Stat label="Corner attesi (casa · ospiti)" value={`${fmt(c.expected_corners)}`} color="#39FF14" />
          <div className="text-right mono text-xs text-[#A1A1AA]">{fmt(c.expected_home)} · {fmt(c.expected_away)}</div>
          {c.reasoning && <p className="mt-3 text-xs text-[#A1A1AA] leading-relaxed border-l-2 border-[#39FF14]/40 pl-3">{c.reasoning}</p>}
        </Card>

        <Card title="Cartellini" testId="cards-card">
          <div className="space-y-3">
            <Bar label="Over 2.5" value={ca.over_2_5_cards} />
            <Bar label="Over 3.5" value={ca.over_3_5_cards} />
            <Bar label="Over 4.5" value={ca.over_4_5_cards} />
            <Bar label="Espulsione" value={ca.red_card_probability} />
          </div>
          <Stat label="Gialli attesi" value={fmt(ca.expected_yellow_cards)} color="#FFCC00" />
        </Card>
      </div>
    </div>
  );
}
